/**
 * DashboardTab — executive dashboard cho Production Report module
 * (spec §8 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Modern-web layout:
 *  - Title row text-only (no gradient bar).
 *  - 5 KPI cards (white bg, accent stripe left).
 *  - 6 chart cards: title text + subtle border, no colored header block.
 *  - Single accent palette (primary purple + success green + danger red),
 *    chart colors share 1 palette để giảm noise.
 *
 * Charts dùng SVG/divs inline (không thêm dependency mới). Khi BE expose
 * `/api/production-report/{po_no}/dashboard` thì swap mock dataset.
 */
import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
  CATEGORY_ACTUAL_VS_BOM,
  DASHBOARD_KPIS,
  ITEM_ACTUAL_VS_BOM,
  OUTPUT_CATEGORY_DISTRIBUTION,
  OUTPUT_ITEMS_MT,
  QUALITY_OUTPUT_VS_INPUT_LEFT,
  QUALITY_OUTPUT_VS_INPUT_RIGHT,
} from '../productionReportMock'
import { COLORS, kpiAccent, type KpiTone } from './styleTokens'

interface Props {
  poNo: string
  poType: string
}

/* ---------- formatters --------------------------------------------------- */

function fmtMt(v: number): string {
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtPct(v: number): string {
  return (v * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%'
}
function fmtUsd(v: number): string {
  return '$ ' + Math.round(v).toLocaleString('en-US')
}

/* ---------- KPI card ----------------------------------------------------- */

interface KpiCardProps {
  label: string
  value: string
  tone?: KpiTone
  subtitle?: string
}

function KpiCard({ label, value, tone = 'neutral', subtitle }: KpiCardProps) {
  const stripe = kpiAccent(tone)
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 170,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1.5,
        bgcolor: COLORS.bgPage,
        px: 2,
        py: 1.5,
        borderLeft: `3px solid ${stripe}`,
      }}
    >
      <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, color: COLORS.text, lineHeight: 1.2, mt: 0.5 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography sx={{ fontSize: '0.72rem', color: COLORS.textSubtle, mt: 0.25 }}>{subtitle}</Typography>
      ) : null}
    </Box>
  )
}

/* ---------- chart card --------------------------------------------------- */

interface ChartCardProps {
  title: string
  children: React.ReactNode
  height?: number
}

function ChartCard({ title, children, height = 240 }: ChartCardProps) {
  return (
    <Box
      sx={{
        flex: '1 1 360px',
        minWidth: 300,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1.5,
        bgcolor: COLORS.bgPage,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${COLORS.border}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: COLORS.text, letterSpacing: 0.2 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 1.5, height, display: 'flex', alignItems: 'stretch' }}>{children}</Box>
    </Box>
  )
}

/* ---------- Doughnut (SVG) ----------------------------------------------- */

// Palette dùng chung cho doughnut/category slices — desaturated, web-friendly.
const SLICE_PALETTE = ['#7c3aed', '#0ea5e9', '#f59e0b', '#ef4444', '#14b8a6', '#64748b']

function Doughnut({ data }: { data: { category: string; mt: number }[] }) {
  const total = data.reduce((s, d) => s + d.mt, 0)
  const radius = 64
  const cx = 80
  const cy = 80
  const inner = 40
  const offsets = data.reduce<number[]>((acc, d) => {
    const prev = acc[acc.length - 1] ?? 0
    acc.push(prev + d.mt)
    return acc
  }, [])
  const slices = data.map((d, i) => {
    const startTotal = i === 0 ? 0 : offsets[i - 1]
    const endTotal = offsets[i]
    const start = (startTotal / total) * Math.PI * 2
    const end = (endTotal / total) * Math.PI * 2
    const large = end - start > Math.PI ? 1 : 0
    const x1 = cx + Math.cos(start - Math.PI / 2) * radius
    const y1 = cy + Math.sin(start - Math.PI / 2) * radius
    const x2 = cx + Math.cos(end - Math.PI / 2) * radius
    const y2 = cy + Math.sin(end - Math.PI / 2) * radius
    const x3 = cx + Math.cos(end - Math.PI / 2) * inner
    const y3 = cy + Math.sin(end - Math.PI / 2) * inner
    const x4 = cx + Math.cos(start - Math.PI / 2) * inner
    const y4 = cy + Math.sin(start - Math.PI / 2) * inner
    const path = `M ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`
    return { path, color: SLICE_PALETTE[i % SLICE_PALETTE.length], d }
  })
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      <svg width={160} height={160} viewBox="0 0 160 160">
        {slices.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke="#ffffff" strokeWidth={1.5} />
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="10" fill={COLORS.textMuted}>
          Total
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="13" fontWeight={700} fill={COLORS.text}>
          {fmtMt(total)}
        </text>
      </svg>
      <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
        {data.map((d, i) => (
          <Stack key={d.category} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: SLICE_PALETTE[i % SLICE_PALETTE.length], flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8125rem', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: COLORS.text }}>
              {d.category}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.textMuted }}>
              {fmtMt(d.mt)}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: COLORS.textSubtle, width: 48, textAlign: 'right' }}>
              {((d.mt / total) * 100).toFixed(1)}%
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

/* ---------- Horizontal bar list ----------------------------------------- */

function BarList({ data, valueFormatter }: { data: { label: string; value: number }[]; valueFormatter?: (v: number) => string }) {
  const max = Math.max(...data.map((d) => d.value), 0.01)
  return (
    <Stack spacing={0.75} sx={{ width: '100%', overflowY: 'auto', pr: 0.5 }}>
      {data.map((d) => (
        <Box key={d.label}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.text }}>{d.label}</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.textMuted }}>
              {valueFormatter ? valueFormatter(d.value) : fmtMt(d.value)}
            </Typography>
          </Stack>
          <Box sx={{ position: 'relative', height: 6, borderRadius: 999, bgcolor: COLORS.bgHeader }}>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${(d.value / max) * 100}%`,
                bgcolor: COLORS.primary,
                borderRadius: 999,
              }}
            />
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

/* ---------- Grouped bar (Actual vs BOM / Output vs Input) --------------- */

function GroupedBar({
  data,
  leftLabel,
  rightLabel,
  leftKey,
  rightKey,
  isPercent = false,
}: {
  data: Array<Record<string, string | number>>
  leftLabel: string
  rightLabel: string
  leftKey: string
  rightKey: string
  isPercent?: boolean
}) {
  const leftColor = COLORS.primary
  const rightColor = COLORS.borderStrong
  const max = Math.max(
    ...data.map((d) => Math.max(Number(d[leftKey]) || 0, Number(d[rightKey]) || 0)),
    0.01,
  )
  const fmt = (v: number) => (isPercent ? v.toFixed(1) + '%' : v.toFixed(2))
  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, bgcolor: leftColor, borderRadius: 0.3 }} />
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>{leftLabel}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, bgcolor: rightColor, borderRadius: 0.3 }} />
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>{rightLabel}</Typography>
        </Stack>
      </Stack>
      <Stack spacing={0.6} sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
        {data.map((d) => {
          const lv = Number(d[leftKey]) || 0
          const rv = Number(d[rightKey]) || 0
          return (
            <Box key={String(d.label)}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: COLORS.text }}>{String(d.label)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted }}>
                  {fmt(lv)} <Box component="span" sx={{ color: COLORS.textSubtle, mx: 0.25 }}>vs</Box> {fmt(rv)}
                </Typography>
              </Stack>
              <Stack spacing={0.25}>
                <Box sx={{ height: 5, borderRadius: 999, bgcolor: alpha(leftColor, 0.1), position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(lv / max) * 100}%`, bgcolor: leftColor, borderRadius: 999 }} />
                </Box>
                <Box sx={{ height: 5, borderRadius: 999, bgcolor: alpha(rightColor, 0.2), position: 'relative' }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(rv / max) * 100}%`, bgcolor: rightColor, borderRadius: 999 }} />
                </Box>
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function DashboardTab({ poNo, poType }: Props) {
  const k = DASHBOARD_KPIS
  const pnlTone: KpiTone = k.profitLoss >= 0 ? 'success' : 'danger'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Title row — text-only, no gradient. */}
      <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.text, letterSpacing: 0.2 }}>
          Batch summary
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textMuted }}>
          PO {poNo} · Type {poType}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={2}>
          <Stack direction="row" spacing={0.5} alignItems="baseline">
            <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              Out-turn
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.success }}>{fmtPct(k.outTurn)}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="baseline">
            <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
              Loss
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.danger }}>{fmtPct(k.loss)}</Typography>
          </Stack>
        </Stack>
      </Stack>

      {/* KPI cards */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <KpiCard label="Input net weight" value={`${fmtMt(k.inputNetWeight)} Mt`} tone="primary" />
        <KpiCard label="Output net weight" value={`${fmtMt(k.outputNetWeight)} Mt`} tone="success" />
        <KpiCard label="Total value in" value={fmtUsd(k.totalValueIn)} />
        <KpiCard label="Total value out" value={fmtUsd(k.totalValueOut)} />
        <KpiCard
          label="Profit & loss"
          value={fmtUsd(k.profitLoss)}
          tone={pnlTone}
          subtitle={k.profitLoss >= 0 ? 'Above expected' : 'Below expected'}
        />
      </Stack>

      {/* Row 1: Doughnut + Item Mt bar */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <ChartCard title="Output category distribution">
          <Doughnut data={OUTPUT_CATEGORY_DISTRIBUTION} />
        </ChartCard>
        <ChartCard title="Output items (Mt)">
          <BarList data={OUTPUT_ITEMS_MT.map((d) => ({ label: d.item, value: d.mt }))} />
        </ChartCard>
      </Stack>

      {/* Row 2: Category + Item Actual vs BOM */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <ChartCard title="Category — Actual vs BOM %">
          <GroupedBar
            data={CATEGORY_ACTUAL_VS_BOM.map((d) => ({ label: d.label, actual: d.actual, bom: d.bom }))}
            leftLabel="Actual"
            rightLabel="BOM"
            leftKey="actual"
            rightKey="bom"
            isPercent
          />
        </ChartCard>
        <ChartCard title="Item — Actual vs BOM %">
          <GroupedBar
            data={ITEM_ACTUAL_VS_BOM.map((d) => ({ label: d.label, actual: d.actual, bom: d.bom }))}
            leftLabel="Actual"
            rightLabel="BOM"
            leftKey="actual"
            rightKey="bom"
            isPercent
          />
        </ChartCard>
      </Stack>

      {/* Row 3: Quality output vs input */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <ChartCard title="Quality — Output vs Input (defects)">
          <GroupedBar
            data={QUALITY_OUTPUT_VS_INPUT_LEFT.map((d) => ({ label: d.label, output: d.output, input: d.input }))}
            leftLabel="Output"
            rightLabel="Input"
            leftKey="output"
            rightKey="input"
          />
        </ChartCard>
        <ChartCard title="Quality — Output vs Input (screens)">
          <GroupedBar
            data={QUALITY_OUTPUT_VS_INPUT_RIGHT.map((d) => ({ label: d.label, output: d.output, input: d.input }))}
            leftLabel="Output"
            rightLabel="Input"
            leftKey="output"
            rightKey="input"
          />
        </ChartCard>
      </Stack>
    </Box>
  )
}
